import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function ChangePartnerSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeIcon, setHeroBadgeIcon] = useState("RefreshCw");
  const [heroBadgeText, setHeroBadgeText] = useState("Partner Transition");
  const [heroHeading, setHeroHeading] = useState("Change Your");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("SAP Partner");
  const [heroDescription, setHeroDescription] = useState("");
  const [heroCtaText, setHeroCtaText] = useState("Book a Free Assessment");
  const [heroCtaLink, setHeroCtaLink] = useState("/contact");

  /* ---------------- REASONS ---------------- */
  const [reasonsActive, setReasonsActive] = useState(true);
  const [reasonsHeading, setReasonsHeading] = useState(
    "Why Businesses Switch Partners",
  );
  const [reasonsDescription, setReasonsDescription] = useState(
    "If any of these sound familiar, it might be time to consider a change.",
  );
  const [reasonsCards, setReasonsCards] = useState([
    { icon: "", title: "", description: "" },
  ]);

  /* ---------------- TRANSITION PROCESS ---------------- */
  const [processActive, setProcessActive] = useState(true);
  const [processHeading, setProcessHeading] = useState(
    "Our Smooth Transition Process",
  );
  const [processDescription, setProcessDescription] = useState(
    "We've helped dozens of businesses switch SAP partners without any disruption.",
  );
  const [processSteps, setProcessSteps] = useState([
    { step: 1, title: "", description: "" },
  ]);

  /* ---------------- GUARANTEE ---------------- */
  const [guarActive, setGuarActive] = useState(true);
  const [guarBadgeIcon, setGuarBadgeIcon] = useState("Shield");
  const [guarBadgeText, setGuarBadgeText] = useState("Our Commitment");
  const [guarHeading, setGuarHeading] = useState(
    "The ThynkProcess Partner Guarantee",
  );
  const [guarDescription, setGuarDescription] = useState("");
  const [guarItems, setGuarItems] = useState([""]);
  // Right card
  const [guarCardHeading, setGuarCardHeading] = useState(
    "Ready to Make the Switch?",
  );
  const [guarCardDescription, setGuarCardDescription] = useState("");
  const [guarCardChecklist, setGuarCardChecklist] = useState([""]);
  const [guarCardCtaText, setGuarCardCtaText] = useState(
    "Book Free Assessment",
  );
  const [guarCardCtaLink, setGuarCardCtaLink] = useState("/contact");
  const [guarCardPhone, setGuarCardPhone] = useState("+61 3 7075 3900");

  /* ---------------- FAQ PREVIEW ---------------- */
  const [faqActive, setFaqActive] = useState(true);
  const [faqHeading, setFaqHeading] = useState(
    "Have Questions About Switching?",
  );
  const [faqDescription, setFaqDescription] = useState(
    "Visit our FAQ for answers to common questions about the partner transition process.",
  );
  const [faqCtaText, setFaqCtaText] = useState("View FAQ");
  const [faqCtaLink, setFaqCtaLink] = useState("/resources/faq");

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeIcon(d.hero.badge_icon || "RefreshCw");
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      setHeroCtaText(d.hero.cta_text || "");
      setHeroCtaLink(d.hero.cta_link || "");
    }

    if (d.reasons) {
      setReasonsHeading(d.reasons.heading || "");
      setReasonsDescription(d.reasons.description || "");
      if (d.reasons.cards?.length) setReasonsCards(d.reasons.cards);
    }

    if (d.transition_process) {
      setProcessHeading(d.transition_process.heading || "");
      setProcessDescription(d.transition_process.description || "");
      if (d.transition_process.steps?.length)
        setProcessSteps(d.transition_process.steps);
    }

    if (d.guarantee) {
      setGuarBadgeIcon(d.guarantee.badge_icon || "Shield");
      setGuarBadgeText(d.guarantee.badge_text || "");
      setGuarHeading(d.guarantee.heading || "");
      setGuarDescription(d.guarantee.description || "");
      if (d.guarantee.items?.length) setGuarItems(d.guarantee.items);
      setGuarCardHeading(d.guarantee.card_heading || "");
      setGuarCardDescription(d.guarantee.card_description || "");
      if (d.guarantee.card_checklist?.length)
        setGuarCardChecklist(d.guarantee.card_checklist);
      setGuarCardCtaText(d.guarantee.card_cta_text || "");
      setGuarCardCtaLink(d.guarantee.card_cta_link || "");
      setGuarCardPhone(d.guarantee.card_phone || "");
    }

    if (d.faq_preview) {
      setFaqHeading(d.faq_preview.heading || "");
      setFaqDescription(d.faq_preview.description || "");
      setFaqCtaText(d.faq_preview.cta_text || "");
      setFaqCtaLink(d.faq_preview.cta_link || "");
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_icon: heroBadgeIcon,
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      cta_text: heroCtaText,
      cta_link: heroCtaLink,
    },
    reasons: {
      heading: reasonsHeading,
      description: reasonsDescription,
      cards: reasonsCards,
    },
    transition_process: {
      heading: processHeading,
      description: processDescription,
      steps: processSteps,
    },
    guarantee: {
      badge_icon: guarBadgeIcon,
      badge_text: guarBadgeText,
      heading: guarHeading,
      description: guarDescription,
      items: guarItems,
      card_heading: guarCardHeading,
      card_description: guarCardDescription,
      card_checklist: guarCardChecklist,
      card_cta_text: guarCardCtaText,
      card_cta_link: guarCardCtaLink,
      card_phone: guarCardPhone,
    },
    faq_preview: {
      heading: faqHeading,
      description: faqDescription,
      cta_text: faqCtaText,
      cta_link: faqCtaLink,
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeIcon,
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroCtaText,
    heroCtaLink,
    reasonsHeading,
    reasonsDescription,
    reasonsCards,
    processHeading,
    processDescription,
    processSteps,
    guarBadgeIcon,
    guarBadgeText,
    guarHeading,
    guarDescription,
    guarItems,
    guarCardHeading,
    guarCardDescription,
    guarCardChecklist,
    guarCardCtaText,
    guarCardCtaLink,
    guarCardPhone,
    faqHeading,
    faqDescription,
    faqCtaText,
    faqCtaLink,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Badge Icon">
            <SelectInput
              value={heroBadgeIcon}
              options={ICON_OPTIONS}
              onChange={(e) => setHeroBadgeIcon(e.target.value)}
            />
          </Field>
          <Field label="Badge Text">
            <TextInput
              value={heroBadgeText}
              onChange={(e) => setHeroBadgeText(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={heroCtaText}
              onChange={(e) => setHeroCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={heroCtaLink}
              onChange={(e) => setHeroCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* REASONS */}
      <CollapsibleSection
        title="Why Businesses Switch"
        active={reasonsActive}
        onToggleActive={setReasonsActive}
      >
        <Field label="Heading">
          <TextInput
            value={reasonsHeading}
            onChange={(e) => setReasonsHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={reasonsDescription}
            onChange={(e) => setReasonsDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Reason Cards</p>
          <button
            onClick={() =>
              setReasonsCards((s) => [
                ...s,
                { icon: "", title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {reasonsCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={reasonsCards.length}
                  onMove={(f, t) =>
                    setReasonsCards(moveItem(reasonsCards, f, t))
                  }
                />
                {reasonsCards.length > 1 && (
                  <button
                    onClick={() =>
                      setReasonsCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setReasonsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setReasonsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setReasonsCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* TRANSITION PROCESS */}
      <CollapsibleSection
        title="Transition Process"
        active={processActive}
        onToggleActive={setProcessActive}
      >
        <Field label="Heading">
          <TextInput
            value={processHeading}
            onChange={(e) => setProcessHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={processDescription}
            onChange={(e) => setProcessDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Steps</p>
          <button
            onClick={() =>
              setProcessSteps((s) => [
                ...s,
                { step: s.length + 1, title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Step
          </button>
        </div>
        {processSteps.map((s, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Step {s.step}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={processSteps.length}
                  onMove={(f, t) =>
                    setProcessSteps(
                      moveItem(processSteps, f, t).map((x, idx) => ({
                        ...x,
                        step: idx + 1,
                      })),
                    )
                  }
                />
                {processSteps.length > 1 && (
                  <button
                    onClick={() =>
                      setProcessSteps((st) =>
                        st
                          .filter((_, idx) => idx !== i)
                          .map((x, idx) => ({ ...x, step: idx + 1 })),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={s.title}
                onChange={(e) =>
                  setProcessSteps((st) =>
                    st.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={s.description}
                onChange={(e) =>
                  setProcessSteps((st) =>
                    st.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* GUARANTEE */}
      <CollapsibleSection
        title="Guarantee Section"
        active={guarActive}
        onToggleActive={setGuarActive}
      >
        <p className="text-xs font-semibold text-muted-foreground mb-3">
          Left Column
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Badge Icon">
            <SelectInput
              value={guarBadgeIcon}
              options={ICON_OPTIONS}
              onChange={(e) => setGuarBadgeIcon(e.target.value)}
            />
          </Field>
          <Field label="Badge Text">
            <TextInput
              value={guarBadgeText}
              onChange={(e) => setGuarBadgeText(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Heading">
          <TextInput
            value={guarHeading}
            onChange={(e) => setGuarHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={guarDescription}
            onChange={(e) => setGuarDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Guarantee Items</p>
          <button
            onClick={() => setGuarItems((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Item
          </button>
        </div>
        {guarItems.map((item, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <div className="flex-1">
              <TextInput
                value={item}
                placeholder={`Guarantee ${i + 1}`}
                onChange={(e) =>
                  setGuarItems((s) =>
                    s.map((x, idx) => (idx === i ? e.target.value : x)),
                  )
                }
              />
            </div>
            {guarItems.length > 1 && (
              <button
                onClick={() =>
                  setGuarItems((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}

        <p className="text-xs font-semibold text-muted-foreground mt-6 mb-3">
          Right Card
        </p>
        <Field label="Card Heading">
          <TextInput
            value={guarCardHeading}
            onChange={(e) => setGuarCardHeading(e.target.value)}
          />
        </Field>
        <Field label="Card Description">
          <TextArea
            value={guarCardDescription}
            onChange={(e) => setGuarCardDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Card Checklist Items</p>
          <button
            onClick={() => setGuarCardChecklist((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Item
          </button>
        </div>
        {guarCardChecklist.map((item, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <div className="flex-1">
              <TextInput
                value={item}
                placeholder={`Checklist item ${i + 1}`}
                onChange={(e) =>
                  setGuarCardChecklist((s) =>
                    s.map((x, idx) => (idx === i ? e.target.value : x)),
                  )
                }
              />
            </div>
            {guarCardChecklist.length > 1 && (
              <button
                onClick={() =>
                  setGuarCardChecklist((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="CTA Text">
            <TextInput
              value={guarCardCtaText}
              onChange={(e) => setGuarCardCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={guarCardCtaLink}
              onChange={(e) => setGuarCardCtaLink(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Phone Number">
          <TextInput
            value={guarCardPhone}
            placeholder="+61 3 7075 3900"
            onChange={(e) => setGuarCardPhone(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* FAQ PREVIEW */}
      <CollapsibleSection
        title="FAQ Preview"
        active={faqActive}
        onToggleActive={setFaqActive}
      >
        <Field label="Heading">
          <TextInput
            value={faqHeading}
            onChange={(e) => setFaqHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={faqDescription}
            onChange={(e) => setFaqDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={faqCtaText}
              onChange={(e) => setFaqCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={faqCtaLink}
              onChange={(e) => setFaqCtaLink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>
    </>
  );
}
